import {Component, Input, OnInit} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { uniqBy } from 'lodash';
import {
  CapacityInfo,
  TrafficConfig,
  TrafficLabelId,
  TrafficSpeed,
} from 'src/app/core';

enum IconGroupType {
  GENERAL = 'GENERAL',
  VPN = 'VPN',
  REMAINING = 'REMAINING',
  GROUPED = 'GROUPED',
}

@Component({
  selector: 'app-social-v2',
  templateUrl: './social-v2.component.html',
  styleUrls: ['./social.component.less'],
})
export class SocialV2Component implements OnInit {
  IconGroupType = IconGroupType;
  @Input() truncateData = false;
  @Input() iconSize: 'small' | 'medium' | 'large' = 'small';
  @Input() showTrafficName = false;
  @Input() showCapacities = false;
  @Input() showSpeed = false;
  @Input() showUsage = false;
  TrafficLabelId = TrafficLabelId;
  isAllDataSelected = false;
  GLOBAL_SOCIAL_PREFIX = 'SOCIAL.TOOLTIPS.';
  GLOBAL_SOCIAL_LABEL_PREFIX = 'SOCIAL.LABELS.';
  GROUPS_MECTRIC_USAGE_PREFIX = 'PAGE.MOBILE.GROUPS.GROUP_METRICS.USAGE.';
  socialGeneral = [TrafficLabelId.FACEBOOK, TrafficLabelId.TWITTER];
  socialVpn = [TrafficLabelId.VPN_BRANDVNO];
  socialRemaining = [TrafficLabelId.WHATSAPP];
  visibleSocialCount = 3;
  socials: CapacityInfo[] | TrafficConfig[] = [];
  lastSocialData: CapacityInfo | TrafficConfig = null;
  private _socialData: CapacityInfo[] | TrafficConfig[];

  get socialData(): CapacityInfo[] | TrafficConfig[] {
    return this._socialData;
  }

  @Input() set socialData(socialData: CapacityInfo[] | TrafficConfig[]) {
    this._socialData = socialData;
    this.socials = socialData;

    if (socialData && socialData.length > 0) {
      this._uniqueTrafficLabels();
    }
  }

  constructor(private translateService: TranslateService) {}

  ngOnInit() {
    this.filterGroupedSocialIcons();
    this.lastSocialData = this.socials[this.socials.length - 1];
  }

  removeSocialFromArray(index: number, checked: boolean): boolean {
    if (checked) {
      this.socials.splice(index, 1);
    }

    return true;
  }

  filterGroupedSocialIcons() {
    let checked = false;
    if (typeof this.socials !== 'undefined') {
      // Exclude extra GENERAL (SPEED_MAX) traffic label
      this.socials = (this.socials as any)
        .filter(socialDatum => !(socialDatum.trafficLabelId === TrafficLabelId.GENERAL &&
          socialDatum.speed === TrafficSpeed.SPEED_MAX));

      this._uniqueTrafficLabels();
      this._isPlanDataControlAllDataSelected();
      this._setTrafficLabelIconsCount();

      this.socials.forEach(
        (data: CapacityInfo | TrafficConfig, index: number) => {
          switch (data.trafficLabelId) {
            case TrafficLabelId.FACEBOOK:
              checked = this.removeSocialFromArray(index, checked);
              break;
            case TrafficLabelId.TWITTER:
              checked = this.removeSocialFromArray(index, checked);
              break;
            default:
              break;
          }
        }
      );
    }
  }

  private _uniqueTrafficLabels(): void {
    const socials = [];

    this.socials.forEach(trafficLabel => {
      let iconGroup = null;
      if (this.socialGeneral.includes(trafficLabel.trafficLabelId)) {
        iconGroup = IconGroupType.GENERAL;
      } else if (this.socialVpn.includes(trafficLabel.trafficLabelId)) {
        iconGroup = IconGroupType.VPN;
      } else if (this.socialRemaining.includes(trafficLabel.trafficLabelId)) {
        iconGroup = IconGroupType.REMAINING;
      } else {
        iconGroup = IconGroupType.GROUPED;
      }
      socials.push({
        ...trafficLabel,
        iconGroup,
      });
    });

    // Only display trafficLabel icon once
    // (same trafficLabel can be used for different control data types)
    this.socials = uniqBy(
      socials,
      trafficLabel => trafficLabel.trafficLabelId && trafficLabel['iconGroup'],
    );
  }

  /**
   * Fix for Plan creation summary view
   */
  private _isPlanDataControlAllDataSelected(): void {
    if (this.socials) {
      const isOnlyGeneralIncluded = (this.socials as any)
        .every(social => social.trafficLabelId === TrafficLabelId.GENERAL);
      const isGeneralWithDefinedSpeedIncluded = (this.socials as any)
        .find(social => social.trafficLabelId === TrafficLabelId.GENERAL &&
          social.speed === TrafficSpeed.SPEED_512KBPS);

      if (isOnlyGeneralIncluded && isGeneralWithDefinedSpeedIncluded) {
        const allData = [];
        this.isAllDataSelected = true;

        Object.keys(TrafficLabelId)
          .filter(trafficLabelId => trafficLabelId === TrafficLabelId.GENERAL.toUpperCase())
          .forEach(trafficLabelId => {
            allData.push({
              ...this.socials[0],
              trafficLabelName: this.translateService
                .instant(this.GLOBAL_SOCIAL_LABEL_PREFIX + trafficLabelId),
            });
          });

        this.socials = allData;
      }
    }
  }

  private _setTrafficLabelIconsCount(): void {
    if (this.isAllDataSelected) { return; }

    let logosCount = 0;
    this.socials = (this.socials as any).map(data => {
      if (this.socialVpn.includes(data.trafficLabelId)) {
        logosCount += this.socialVpn.length;
      } else if (data.trafficLabelId === TrafficLabelId.FACEBOOK) {
        logosCount += 2; // FB and Messenger
      } else if (data.trafficLabelId === TrafficLabelId.TWITTER) {
        logosCount++;
      } else if (this.socialRemaining.includes(data.trafficLabelId)) {
        logosCount += this.socialRemaining.length;
      } else {
        logosCount++;
      }

      return {
        ...data,
        logosCount,
      };
    });
  }
}
